import React, { useState, useEffect, useMemo } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Stack,
} from '@mui/material';
import { LightUserDto, PermissionArea } from '@cloudcost/shared-types/users';
import { getUser } from '@/lib/common';
import { Loader } from '@/components/loader/loader';
import AnnualBudgetChart from '../charts/annual-budget-chart';
import { AppCard } from '@/components/app-card/app-card';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';

import { useSystems } from './hooks/useSystems';
import { SystemsTable } from './systems/systems-table';
import { formatCurrencyNoUnit } from '@/lib/formatters';
import { DepartmentBudgetCard } from './systems/department-budget-card';
import { CreateSystemModal } from './systems/create-system-modal';
import { EditSystemModal } from './systems/edit-system-modal';
import { SetBudgetModal } from './global/set-budget-modal';
import {
  SystemData,
  validateAllDepartmentBudgets,
  getEffectiveBudgetValue,
  getEffectiveBudgetCurrency,
} from '../calculations';
import { useBudgetChanges } from './hooks/useBudgetChanges';
import { SystemFormData, Department } from '../types';
import { hasUserAccessTo, refreshUserRoles } from '@/lib/permissions';
import { BudgetChangeModal, BudgetChangeData } from './budget-change-modal';
import { DepartmentSelector } from './systems/department-selector';
import { useGlobalBudget } from './hooks/useGlobalBudget';

interface BudgetFormData {
  id: number;
  name: string;
  original_currency_value: string;
  original_currency: string | null;
  budget_id?: number;
}

export default function DepartmentBudgetSplit() {
  const { globalBudget } = useGlobalBudget();

  const [alert, setAlert] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [open, setOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  const [users, setUsers] = useState<LightUserDto[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [systemFormData, setSystemFormData] = useState<SystemFormData>({
    name: '',
    systemowner: null,
    department_id: null,
  });
  const [editFormData, setEditFormData] = useState<SystemFormData>({
    name: '',
    systemowner: null,
    department_id: null,
  });
  const [budgetFormData, setBudgetFormData] = useState<BudgetFormData>({
    id: 0,
    name: '',
    original_currency_value: '',
    original_currency: globalBudget?.original_currency || null,
  });

  const [validationErrorOpen, setValidationErrorOpen] = useState(false);
  const [budgetChangeModalOpen, setBudgetChangeModalOpen] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState<SystemData | null>(null);

  const [departmentOwnerId, setDepartmentOwnerId] = useState<number>();
  const [loading, setLoading] = useState(true);
  const [departmentBudgets, setDepartmentBudgets] = useState<
    Map<number, { value: number; currency: string; name: string }>
  >(new Map());
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<
    number | null
  >(null);

  const user = getUser();

  const canViewSystemList =
    hasUserAccessTo(PermissionArea.SYSTEM_VIEW_ALL) ||
    hasUserAccessTo(PermissionArea.SYSTEM_VIEW_DEPARTMENT) ||
    hasUserAccessTo(PermissionArea.SYSTEM_VIEW_OWNED);

  const canSetSystemBudget = (system: SystemData): boolean => {
    if (user?.is_admin) return true;
    // Department owners can set system budgets for systems in departments they own
    const systemDepartment = departments.find(
      (dept) => dept.id === system.department_id,
    );
    return systemDepartment?.id_owner === user?.id;
  };

  // Filter available departments based on user permissions
  const availableDepartments = useMemo(() => {
    const isAdmin = hasUserAccessTo(PermissionArea.ADMIN_PANEL);
    const isSponsor = hasUserAccessTo(PermissionArea.SYSTEM_VIEW_ALL);

    if (isAdmin || isSponsor) {
      return departments; // Admin and sponsor see all departments
    }

    // Regular users see departments they own
    return departments.filter((dept) => dept.id_owner === user?.id);
  }, [departments, user]);

  // Set default selected department
  useEffect(() => {
    if (availableDepartments.length > 0 && !selectedDepartmentId) {
      // Find departments that the user owns (using department.owner_id)
      const userOwnedDepartments = availableDepartments.filter(
        (dept) => dept.id_owner === user?.id,
      );

      if (userOwnedDepartments.length > 0) {
        // If user owns departments, select the first one
        setSelectedDepartmentId(userOwnedDepartments[0].id);
      } else {
        // Otherwise select first available department
        setSelectedDepartmentId(availableDepartments[0].id);
      }
    }
  }, [availableDepartments, user?.id, selectedDepartmentId]);

  const { items, refreshData, setItems } =
    useSystems();

  // Filter systems and chart items based on selected department
  const filteredItems = useMemo(() => {
    if (!selectedDepartmentId) return items;
    return items.filter(
      (system) => system.department_id === selectedDepartmentId,
    );
  }, [items, selectedDepartmentId]);

  const {
    pendingBudgetChanges,
    bulkSaveLoading,
    addPendingChange,
    clearPendingChanges,
    bulkSaveSystemBudgets,
  } = useBudgetChanges({
    bulkSaveUrl: urls.DevOps.bulkSaveSystemBudgets,
  });

  const openConfirmation = () => {
    setConfirmation(true);
  };

  const closeConfirmation = () => {
    setConfirmation(false);
  };

  const handleEditSystem = (system: SystemData) => {
    setEditFormData({
      id: system.id,
      name: system.name,
      systemowner: system.owner_id,
      department_id: system.department_id,
    });
    setEditModalOpen(true);
  };

  const getUserDepartmentBudget = (): {
    value: string;
    currency: string;
    name: string;
  } => {
    const departmentId = selectedDepartmentId;

    if (!departmentId) {
      return { value: 'No department selected', currency: 'N/A', name: 'N/A' };
    }

    const departmentBudget = departmentBudgets.get(departmentId);
    if (!departmentBudget) {
      return { value: 'No budget set', currency: 'N/A', name: 'N/A' };
    }

    return {
      value: formatCurrencyNoUnit(departmentBudget.value),
      currency: departmentBudget.currency,
      name: departmentBudget.name,
    };
  };

  const handleSetBudget = (system: SystemData) => {
    const effectiveValue = getEffectiveBudgetValue(
      system,
      pendingBudgetChanges,
    );
    // comented out for now
    // const effectiveCurrency = getEffectiveBudgetCurrency(
    //   system,
    //   pendingBudgetChanges,
    // );

    const effectiveCurrency = globalBudget?.original_currency || 'USD';

    if (system.value && !pendingBudgetChanges.has(system.id)) {
      setSelectedSystem(system);
      setBudgetChangeModalOpen(true);
    } else {
      setBudgetFormData({
        id: system.id,
        name: system.name,
        original_currency_value: effectiveValue|| '',
        original_currency: effectiveCurrency || null,
      });
      setBudgetModalOpen(true);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const [
          usersData,
          departmentsData,
          currenciesData,
          departmentBudgetsData,
        ] = await Promise.all([
          fetchWrapper.get(urls.DevOps.getUsersBusiness),
          fetchWrapper.get(urls.DevOps.getFilterDepartments),
          fetchWrapper.get(urls.DevOps.currencies),
          fetchWrapper.get(urls.DevOps.getDepartments),
        ]);

        setUsers(usersData);
        setDepartments(departmentsData);
        setCurrencies(currenciesData.map((x: any) => x.currency));

        // department owner - find departments owned by the user
        if (departmentsData.length > 0) {
          const userOwnedDepartments = departmentsData.filter(
            (dept: any) => dept.id_owner === user?.id,
          );

          if (userOwnedDepartments.length > 0 && usersData.length > 0) {
            // Use the first owned department for department owner info
            const userDepartment = userOwnedDepartments[0];
            if (userDepartment.id_owner) {
              const departmentOwner = usersData.find(
                (u: any) => u.id === userDepartment.id_owner,
              );
              if (departmentOwner) {
                setDepartmentOwnerId(departmentOwner.id);
              }
            }
          }
        }

        const deptBudgetsMap = new Map();
        departmentBudgetsData.forEach((dept: any) => {
          const yearly =
            dept.original_currency_value != null
              ? Number(dept.original_currency_value)
              : Number(dept.value);
          if (Number.isFinite(yearly) && yearly > 0 && dept.original_currency) {
            deptBudgetsMap.set(dept.id, {
              value: yearly,
              currency: dept.original_currency,
              name: dept.name,
            });
          }
        });
        setDepartmentBudgets(deptBudgetsMap);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [user?.id]);

  const createSystem = () => {
    fetchWrapper
      .post(urls.DevOps.createSystem, {
        name: systemFormData.name,
        systemowner: systemFormData.systemowner,
        department_id: systemFormData.department_id,
      })
      .then(() => {
        refreshData();
        handleClose();
        refreshUserRoles();
      })
      .catch((error) => console.error('Error creating system:', error));
  };

  const editSystem = () => {
    if (!editFormData.name) {
      return;
    }
    if (!editFormData.systemowner) {
      setAlert(true);
      return;
    }
    if (!editFormData.department_id) {
      setAlert(true);
      return;
    }

    fetchWrapper
      .put(urls.DevOps.updateSystem, {
        id: editFormData.id,
        name: editFormData.name,
        systemowner: editFormData.systemowner,
        department_id: editFormData.department_id,
      })
      .then(() => {
        refreshData();
        handleClose();
        refreshUserRoles();
      })
      .catch((error) => console.error('Error updating system:', error));
  };

  const setBudget = () => {
    if (
      !budgetFormData.original_currency_value ||
      !budgetFormData.original_currency
    ) {
      return;
    }

    addPendingChange(budgetFormData.id, {
      id: budgetFormData.id,
      value: budgetFormData.original_currency_value,
      currency: budgetFormData.original_currency,
      budget_id: budgetFormData.budget_id,
    });

    handleClose();
  };

  const handleBulkSave = () => {
    const validateAll = () =>
      validateAllDepartmentBudgets(
        items,
        departmentBudgets,
        pendingBudgetChanges,
      );
    const onValidationError = () => setValidationErrorOpen(true);
    const onSuccess = () => refreshData();

    bulkSaveSystemBudgets(validateAll, onValidationError, onSuccess);
  };

  const deleteSystem = (e: any) => {
    closeConfirmation();
    if (editFormData.id) {
      fetchWrapper
        .delete(urls.DevOps.deleteSystem, {
          id: editFormData.id,
        })
        .then(() => {
          fetchWrapper
            .get(urls.DevOps.allsystems)
            .then((data) => setItems(data))
            .catch((error) => console.error('There was an error!', error));

          setItems(items.filter((row: any) => row.id !== editFormData.id));
          handleClose();
          refreshUserRoles();
        })
        .catch((error) => console.error('There was an error!', error));
    }
  };

  const handleBudgetChangeSubmit = async (data: BudgetChangeData) => {
    if (!selectedSystem) return;

    try {
      await fetchWrapper.post(urls.DevOps.createSystemBudgetChangeRequest, {
        systemId: selectedSystem.id,
        currency: selectedSystem.original_currency || 'USD',
        newBudgetValue: data.newAnnualBudget,
        reason: data.reason,
        requesterId: user?.id,
      });

      refreshData();

      setBudgetChangeModalOpen(false);
      setSelectedSystem(null);
    } catch (error) {
      console.error('Error processing system budget change:', error);
    }
  };

  const handleOpen2 = () => {
    setSystemFormData({
      name: '',
      systemowner: null,
      department_id: null,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setAlert(false);
    setOpen(false);
    setEditModalOpen(false);
    setBudgetModalOpen(false);
    setBudgetChangeModalOpen(false);
    setSelectedSystem(null);

    setSystemFormData({
      name: '',
      systemowner: null,
      department_id: null,
    });

    setEditFormData({
      name: '',
      systemowner: null,
      department_id: null,
    });

    setBudgetFormData({
      id: 0,
      name: '',
      original_currency_value: '',
      original_currency: null,
    });
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Stack spacing={2}>
      {canViewSystemList && (
        <>
          <DepartmentSelector
            departments={availableDepartments}
            selectedDepartmentId={selectedDepartmentId}
            onDepartmentChange={(departmentId) =>
              setSelectedDepartmentId(departmentId)
            }
            disabled={availableDepartments.length <= 1}
          />

          <DepartmentBudgetCard
            items={filteredItems}
            selectedDepartmentId={selectedDepartmentId?.toString() || ''}
            departmentBudgets={departmentBudgets}
            getUserDepartmentBudget={getUserDepartmentBudget}
          />

          <AppCard
            className="card100"
            title="Annual System Budgets"
            icon={<BarChartOutlinedIcon color="primary" />}>
            <AnnualBudgetChart
              data={filteredItems.map((item) => ({
                name: item.name,
                budgetValue: item.value || 0,
              }))}
              referenceLine={{
                value: selectedDepartmentId
                  ? departmentBudgets.get(selectedDepartmentId)?.value ?? 0
                  : 0,
                label: 'Annual Department Budget',
              }}
              seriesLabel="System Budget"
            />
          </AppCard>

          <SystemsTable
            items={filteredItems}
            pendingBudgetChanges={pendingBudgetChanges}
            departmentBudgets={departmentBudgets}
            onEditSystem={handleEditSystem}
            onSetBudget={handleSetBudget}
            onCreateSystem={handleOpen2}
            onBulkSave={handleBulkSave}
            onCancelChanges={clearPendingChanges}
            bulkSaveLoading={bulkSaveLoading}
            canSetSystemBudget={canSetSystemBudget}
          />
        </>
      )}

      <CreateSystemModal
        open={open}
        onClose={handleClose}
        onSubmit={createSystem}
        users={users}
        departments={departments}
        formData={systemFormData}
        setFormData={setSystemFormData}
      />

      <EditSystemModal
        open={editModalOpen}
        onClose={handleClose}
        onSubmit={editSystem}
        onDelete={openConfirmation}
        users={users}
        departments={departments}
        formData={editFormData}
        setFormData={setEditFormData}
        alert={alert}
      />

      <SetBudgetModal
        open={budgetModalOpen}
        onClose={handleClose}
        onSubmit={setBudget}
        currencies={currencies}
        formData={budgetFormData}
        setFormData={setBudgetFormData}
      />

      <Dialog
        open={confirmation}
        onClose={closeConfirmation}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to delete the system?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Deleting the system will also delete budget history for that system
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmation}>Cancel</Button>
          <Button onClick={deleteSystem} autoFocus data-testid="confirmation-dialog-confirm">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={validationErrorOpen}
        onClose={() => setValidationErrorOpen(false)}
        aria-labelledby="validation-error-dialog-title"
        aria-describedby="validation-error-dialog-description">
        <DialogTitle id="validation-error-dialog-title">
          Budget Validation Error
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="validation-error-dialog-description">
            Sum of allocated System Budgets must not exceed the Department
            Budget!
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationErrorOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      <BudgetChangeModal
        open={budgetChangeModalOpen}
        onClose={() => {
          setBudgetChangeModalOpen(false);
          setSelectedSystem(null);
        }}
        onSubmit={handleBudgetChangeSubmit}
        budgetType="system"
        currentBudget={selectedSystem?.value || 0}
        budgetCurrency={selectedSystem?.original_currency || ''}
        entityName={selectedSystem?.name}
        systemOwner={selectedSystem?.owner}
        departmentBudget={
          departmentBudgets.get(selectedSystem?.department_id || 0)?.value
        }
        departmentsBudgetsSum={
          items
            .filter(
              (sys) => sys.department_id === selectedSystem?.department_id,
            )
            .reduce((sum, sys) => sum + (sys.value || 0), 0) -
          (selectedSystem?.value || 0)
        }
      />
    </Stack>
  );
}
